import { AllReportYearsBase } from 'npo/pages/ReportsBeta/api';
import React, { useCallback, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import IndividualReportSharing from './IndividualReportSharing';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import ReportSharingToggle from './ReportSharingToggle';
import { ReportVisibility } from 'shared/auth/hooks/useUserActions';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { updateProgramVisibility } from './api';
import TrueImpactFloatLoading from 'shared/design-system/components/TrueImpactFloatLoading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';

const tooltip = (
  <>
    You can choose visibility for this program.
    <br />
    All reports under this program will inherit this setting.
  </>
);

const isAllSelected = (reports: AllReportYearsBase[]): ReportVisibility => {
  return reports.every(r => r.visibility === 'PUBLIC')
    ? 'PUBLIC'
    : 'RESTRICTED';
};

export const programDonorNameDisabledReason =
  'Public is unavailable because this program or one of its reports mentions a funder by name. ';

interface ReportSharingProgramProps {
  reports: AllReportYearsBase[];
  handleProgramEnabled: (
    programId: number,
    enabled: ReportVisibility,
  ) => unknown;
  program: {
    Id: number;
    name: string;
    managerId: number;
    level1DonorName?: string | null;
  };
}

const StyledDiv = styled.div<{ showReports: boolean }>`
  margin-top: ${SPACERS.small};
  margin-bottom: ${SPACERS.small};
  border: 1px solid
    ${({ showReports }) =>
      showReports ? COLORS.ctaPrimary : COLORS.grayBase};
  border-radius: ${SPACERS.tiny};
  background-color: ${COLORS.whiteBase};
  overflow: hidden;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  &:hover {
    border-color: ${COLORS.ctaPrimary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
  .program-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACERS.small};
    padding: ${SPACERS.small} ${SPACERS.medium};
    transition: background-color 0.2s ease-in-out;
    background-color: ${({ showReports }) =>
      showReports ? COLORS.ctaPrimaryBackgroundLighter : COLORS.whiteBase};
    .program-name {
      flex: 1;
      display: flex;
      align-items: center;
      gap: ${SPACERS.tiny};
      min-width: 0;
      cursor: pointer;
      .program-chevron {
        color: ${COLORS.ctaPrimary};
        font-size: 12px;
        width: 12px;
        flex-shrink: 0;
        transition: transform 0.2s ease-in-out;
        transform: rotate(${({ showReports }) => (showReports ? 90 : 0)}deg);
      }
      .program-label {
        color: ${COLORS.grayDark};
        margin-right: ${SPACERS.extraTiny};
      }
      .program-title {
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    .sharing-toggle {
      flex-shrink: 0;
    }
  }
  .reports-container {
    border-top: 1px solid ${COLORS.grayLight};
    background-color: ${COLORS.ctaPrimaryBackgroundLightest};
  }
`;

const ReportSharingProgram: React.FC<ReportSharingProgramProps> = ({
  program,
  reports,
  handleProgramEnabled,
}) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest();
  const [showReports, setShowReports] = useState<boolean>(false);
  const [allPrograms, setAllPrograms] = useState<ReportVisibility>(
    isAllSelected(reports),
  );

  const handleShowReportsToggle = useCallback(() => {
    setShowReports(s => !s);
  }, [setShowReports]);

  const publicDisabled =
    Boolean(program.level1DonorName) || reports.some(r => r.level1DonorName);

  const handleProgramToggle = useCallback(
    (visibility: ReportVisibility) => {
      if (!client) return;
      if (visibility === 'PUBLIC' && publicDisabled) return;
      setAllPrograms(visibility);
      handleProgramEnabled(program.Id, visibility);
      makeRequest(
        updateProgramVisibility(client, {
          programId: program.Id,
          visibility,
        }),
      );
    },
    [client, handleProgramEnabled, program.Id, setAllPrograms, publicDisabled],
  );

  return (
    <StyledDiv showReports={showReports}>
      <TrueImpactFloatLoading
        requestState={requestState}
        loadingMessage="Saving program's visibility"
        successMessage="Program's visibility saved successfully"
        errorMessage="Failed to save program's visibility"
      />
      <div className="program-header">
        <TrueImpactText
          className="program-name"
          allowTextSelection={false}
          onClick={handleShowReportsToggle}
        >
          <FontAwesomeIcon icon={faChevronRight} className="program-chevron" />
          <span className="program-label">Program</span>
          <span className="program-title" title={program.name}>
            {program.name}
          </span>
        </TrueImpactText>
        <ReportSharingToggle
          className="sharing-toggle"
          tooltip={tooltip}
          tooltipMinWidth="380px"
          visibility={allPrograms}
          publicDisabledReason={
            publicDisabled ? programDonorNameDisabledReason : undefined
          }
          handleToggle={handleProgramToggle}
        />
      </div>
      <TrueImpactCollapse show={showReports} bufferHeight={1}>
        <div className="reports-container">
          {reports.map((r, i) => (
            <IndividualReportSharing
              key={r.dbId}
              report={r}
              index={i}
              program={program}
              isLastIndex={i === reports.length - 1}
            />
          ))}
        </div>
      </TrueImpactCollapse>
    </StyledDiv>
  );
};

export default ReportSharingProgram;
